import { useMemo, useRef } from 'react'

type AnyFunction<TArgs extends any[]> = (...args: TArgs) => void

/**
 * Returns a stable debounced callback that runs on the trailing edge only.
 * The latest callback reference is used on invoke.
 */
export default function useDebouncedCallback<TArgs extends any[]>(
  callback: AnyFunction<TArgs>,
  delayMs: number
) {
  const callbackRef = useRef<AnyFunction<TArgs>>(callback)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  callbackRef.current = callback

  const debounced = useMemo(() => {
    const fn: AnyFunction<TArgs> = (...args: TArgs) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null
        callbackRef.current(...args)
      }, delayMs)
    }

    ;(fn as any).cancel = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }

    return fn as AnyFunction<TArgs> & { cancel: () => void }
  }, [delayMs])

  return debounced
}

